import {useCallback, useEffect, useState} from "react";
import {getMerchantBook, isMirroredMerchant, isDebtCabinetMerchant} from "../../../api/admin/merchants";
import {BookRowSettlementSection} from "./BookRowSettlementSection";
import type {MerchantBookResponse, MerchantResponse} from "../../../api/admin/merchants";
import {formatDate} from "../../../utils";

/**
 * Книга кабинета: то, что видно на странице площадки, — здесь.
 *
 * По такому мерчанту система деньги не считает, а ЧИТАЕТ. Отсюда всё
 * устройство блока:
 *
 * — Разбивка по ЧЕТЫРЁМ корзинам, а не одно число: их страница показывает
 *   именно разбивку, и совпадать надо с ней. Баланс мерчанта — Main + Cashback;
 *   Frozen и Insurance в него не входят.
 * — Прибыль ДВУМЯ числами: «начислено» и «из них перенесено на рабочий
 *   баланс». Баланс отвечает «сколько есть», прибыль — «сколько заработано»,
 *   и рисовать их одинаково нельзя.
 * — Возраст у каждой цифры: цифра верна на момент их последней строки и на
 *   момент нашего чтения, и оба момента названы.
 * — Сверка без порога: норма — ровно ноль. Обе стороны приходят из одной
 *   прочитанной строки, поэтому «малого расхождения» здесь не бывает.
 *
 * Все суммы — строки с бэкенда. В число не переводятся нигде: у денег тут
 * четыре знака после точки на их стороне и шесть на нашей.
 */
export function CabinetBookSection({
    merchant,
    token,
    onSettled,
}: {
    merchant: MerchantResponse;
    token: string | null;
    onSettled?: (message: string) => void;
}) {
    const [book, setBook] = useState<MerchantBookResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const mirrored = isMirroredMerchant(merchant);

    const load = useCallback(async () => {
        if (!token || !mirrored) return;
        setLoading(true);
        setError(null);
        try {
            setBook(await getMerchantBook(token, merchant.id));
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause));
        } finally {
            setLoading(false);
        }
    }, [token, mirrored, merchant.id]);

    useEffect(() => {
        void load();
    }, [load]);

    // Мерчант, которого считаем мы, книги не имеет — и блока тоже.
    if (!mirrored) return isDebtCabinetMerchant(merchant)
        ? <BookRowSettlementSection merchantId={merchant.id} token={token} onSettled={onSettled} /> : null;

    return (
        <><section className="merchant-rate-block merchant-external-block">
            <div className="section-header">
                <div>
                    <h3>Книга кабинета</h3>
                    <p className="section-hint">
                        Баланс этого мерчанта не считается, а читается из книги кабинета площадки —
                        построчно, включая движения без заявок. Здесь те же цифры, что на их
                        странице.
                    </p>
                </div>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => void load()} disabled={loading}>
                    {loading ? "Читаем…" : "Обновить"}
                </button>
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}

            {book && (
                <>
                    <div className="trader-meta-grid">
                        <div className="meta-card">
                            <span className="meta-label">Рабочий баланс (Main)</span>
                            <span className="meta-value mono">{book.baskets.main} {book.currency}</span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Кэшбэк</span>
                            <span className="meta-value mono">{book.baskets.cashback} {book.currency}</span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Заморожено</span>
                            <span className="meta-value mono">{book.baskets.frozen} {book.currency}</span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Страховой депозит</span>
                            <span className="meta-value mono">
                                {book.baskets.insurance === null
                                    ? "не отдан"
                                    : `${book.baskets.insurance} ${book.currency}`}
                            </span>
                        </div>
                    </div>
                    <p className="section-hint">
                        Баланс мерчанта — Main + Cashback = <strong>{book.balance} {book.currency}</strong>.
                        Заморозка и страховой депозит в него не входят: замороженные деньги уже ушли
                        с рабочего баланса, и повторный их учёт списал бы каждый вывод дважды.
                    </p>

                    <div className="section-header">
                        <div>
                            <h3>Прибыль команды</h3>
                            <p className="section-hint">
                                Два числа, а не одно: баланс отвечает «сколько есть», прибыль —
                                «сколько заработано». Перенос кэшбэка на рабочий баланс делают руками
                                в интерфейсе площадки, наш контур этого не умеет.
                            </p>
                        </div>
                    </div>
                    <div className="trader-meta-grid">
                        <div className="meta-card">
                            <span className="meta-label">Начислено</span>
                            <span className="meta-value mono">{book.profit.accrued} {book.currency}</span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Из них перенесено на рабочий баланс</span>
                            <span className="meta-value mono">{book.profit.movedToWorking} {book.currency}</span>
                        </div>
                    </div>

                    <div className="trader-meta-grid">
                        <div className="meta-card">
                            <span className="meta-label">Сверка с их последней строкой</span>
                            <span className={`meta-value merchant-vip-value ${book.matches ? "active" : ""}`}>
                                {book.matches
                                    ? "Сходится"
                                    : `Расхождение ${book.diff} ${book.currency}`}
                            </span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">У нас / у них</span>
                            <span className="meta-value mono">
                                {book.ours} / {book.theirs} {book.currency}
                            </span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Данные на момент</span>
                            <span className="meta-value">
                                {book.tailAt ? formatDate(book.tailAt) : "книга ещё не прочитана"}
                            </span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Прочитано</span>
                            <span className="meta-value">
                                {book.observedAt ? formatDate(book.observedAt) : "—"}
                            </span>
                        </div>
                    </div>
                    <p className="section-hint">
                        Храним {book.rowsStored} строк книги кабинета{" "}
                        <span className="mono">{book.source}/{book.accountCode}</span>
                        {book.tailExternalId !== null && (
                            <>, последняя — <span className="mono">#{book.tailExternalId}</span></>
                        )}
                        . Норма сверки — ровно ноль: оба числа приходят из одной прочитанной строки,
                        поэтому любое расхождение здесь — ошибка, а не допуск.
                    </p>
                </>
            )}

            {!book && !loading && !error && (
                <p className="section-hint">Книга кабинета ещё не прочитана.</p>
            )}
        </section><BookRowSettlementSection merchantId={merchant.id} token={token} onSettled={onSettled} /></>
    );
}
